import { computeGeometricAdjacency } from './adjacency.js';
import { edgeId } from './edges.js';
import { discoverLogicalQuadCells } from './quads.js';

function compareIds(a, b) {
  return a < b ? -1 : a > b ? 1 : 0;
}

function rawEdgeFaceCounts(topology) {
  const faceIdsByRawEdgeId = new Map();
  for (const face of topology.faces) {
    for (let index = 0; index < face.vertexIds.length; index++) {
      const rawEdgeId = edgeId(face.vertexIds[index], face.vertexIds[(index + 1) % face.vertexIds.length]);
      const faceIds = faceIdsByRawEdgeId.get(rawEdgeId);
      if (faceIds) faceIds.add(face.id);
      else faceIdsByRawEdgeId.set(rawEdgeId, new Set([face.id]));
    }
  }
  return faceIdsByRawEdgeId;
}

function canonicalizeOpen(cellIds, crossedEdgeIds) {
  const reversedCellIds = [...cellIds].reverse();
  const reversedCrossedEdgeIds = [...crossedEdgeIds].reverse();
  const forwardKey = JSON.stringify([cellIds, crossedEdgeIds]);
  const reverseKey = JSON.stringify([reversedCellIds, reversedCrossedEdgeIds]);
  return reverseKey < forwardKey
    ? { cellIds: reversedCellIds, crossedEdgeIds: reversedCrossedEdgeIds }
    : { cellIds, crossedEdgeIds };
}

function rotate(values, startIndex) {
  return [...values.slice(startIndex), ...values.slice(0, startIndex)];
}

function canonicalizeClosed(cellIds, crossedEdgeIds) {
  let canonical = null;
  for (let startIndex = 0; startIndex < cellIds.length; startIndex++) {
    const forward = {
      cellIds: rotate(cellIds, startIndex),
      crossedEdgeIds: rotate(crossedEdgeIds, startIndex),
    };
    const backwardCellIds = [];
    const backwardCrossedEdgeIds = [];
    for (let offset = 0; offset < cellIds.length; offset++) {
      const index = (startIndex - offset + cellIds.length) % cellIds.length;
      backwardCellIds.push(cellIds[index]);
      backwardCrossedEdgeIds.push(crossedEdgeIds[(index + 1) % crossedEdgeIds.length]);
    }
    for (const candidate of [forward, { cellIds: backwardCellIds, crossedEdgeIds: backwardCrossedEdgeIds }]) {
      if (!canonical || JSON.stringify([candidate.cellIds, candidate.crossedEdgeIds]) < JSON.stringify([canonical.cellIds, canonical.crossedEdgeIds])) {
        canonical = candidate;
      }
    }
  }
  return canonical;
}

function traversalIndexes(topology, adjacency, logicalCells) {
  const geometricEdgesById = new Map(adjacency.edges.map((edge) => [edge.id, edge]));
  const geometricEdgeIdByRawEdgeId = new Map();
  for (const edge of adjacency.edges) {
    for (const rawEdgeId of edge.topologyEdgeIds) {
      const previous = geometricEdgeIdByRawEdgeId.get(rawEdgeId);
      if (previous && previous !== edge.id) {
        throw new Error(`loop cut traversal found ambiguous geometric mapping for raw edge '${rawEdgeId}'`);
      }
      geometricEdgeIdByRawEdgeId.set(rawEdgeId, edge.id);
    }
  }

  const cellById = new Map();
  const boundaryEntriesByCellId = new Map();
  const cellsByGeometricEdgeId = new Map();
  const faceCountsByRawEdgeId = rawEdgeFaceCounts(topology);
  for (const cell of logicalCells.cells) {
    if (cellById.has(cell.id)) throw new Error(`loop cut traversal found duplicate logical cell '${cell.id}'`);
    if (!Array.isArray(cell.boundaryTopologyEdgeIds) || cell.boundaryTopologyEdgeIds.length !== 4) {
      throw new Error(`logical cell '${cell.id}' must have exactly four boundary edges`);
    }
    cellById.set(cell.id, cell);
    const boundaryEntries = cell.boundaryTopologyEdgeIds.map((rawEdgeId) => ({
      rawEdgeId,
      geometricEdgeId: geometricEdgeIdByRawEdgeId.get(rawEdgeId),
    }));
    const seenGeometricEdgeIds = new Set();
    for (const entry of boundaryEntries) {
      if (!entry.geometricEdgeId) continue;
      if (seenGeometricEdgeIds.has(entry.geometricEdgeId)) {
        throw new Error(`logical cell '${cell.id}' has an ambiguous repeated geometric boundary edge '${entry.geometricEdgeId}'`);
      }
      seenGeometricEdgeIds.add(entry.geometricEdgeId);
      const cellIds = cellsByGeometricEdgeId.get(entry.geometricEdgeId);
      if (cellIds) cellIds.push(cell.id);
      else cellsByGeometricEdgeId.set(entry.geometricEdgeId, [cell.id]);
    }
    boundaryEntriesByCellId.set(cell.id, boundaryEntries);
  }
  for (const cellIds of cellsByGeometricEdgeId.values()) cellIds.sort(compareIds);
  return {
    geometricEdgesById,
    cellById,
    boundaryEntriesByCellId,
    cellsByGeometricEdgeId,
    faceCountsByRawEdgeId,
  };
}

function oppositeEntry(indexes, cellId, entryEdgeId) {
  const boundaryEntries = indexes.boundaryEntriesByCellId.get(cellId);
  const entryIndex = boundaryEntries.findIndex((entry) => entry.geometricEdgeId === entryEdgeId);
  if (entryIndex === -1) {
    throw new Error(`logical cell '${cellId}' is not incident to geometric edge '${entryEdgeId}'`);
  }
  const opposite = boundaryEntries[(entryIndex + 2) % boundaryEntries.length];
  if (opposite.geometricEdgeId) return opposite;
  const faceCount = indexes.faceCountsByRawEdgeId.get(opposite.rawEdgeId)?.size ?? 0;
  if (faceCount > 2) {
    throw new Error(`loop cut traversal reached non-manifold raw edge '${opposite.rawEdgeId}'`);
  }
  throw new Error(`logical cell '${cellId}' boundary raw edge '${opposite.rawEdgeId}' has no geometric edge`);
}

function walkFromStart(indexes, startCellId, startEdgeId) {
  const cellIds = [];
  const crossedEdgeIds = [startEdgeId];
  const seenStates = new Map();
  let cellId = startCellId;
  let entryEdgeId = startEdgeId;
  while (true) {
    const stateId = JSON.stringify([cellId, entryEdgeId]);
    if (seenStates.has(stateId)) {
      if (seenStates.get(stateId) === 0) {
        crossedEdgeIds.pop();
        return { status: 'closed', cellIds, crossedEdgeIds };
      }
      throw new Error(`loop cut traversal revisited logical cell '${cellId}' through a different path`);
    }
    seenStates.set(stateId, cellIds.length);
    cellIds.push(cellId);

    const exit = oppositeEntry(indexes, cellId, entryEdgeId);
    crossedEdgeIds.push(exit.geometricEdgeId);
    const nextCellIds = (indexes.cellsByGeometricEdgeId.get(exit.geometricEdgeId) ?? [])
      .filter((candidateCellId) => candidateCellId !== cellId);
    if (nextCellIds.length === 0) return { status: 'open', cellIds, crossedEdgeIds };
    if (nextCellIds.length > 1) {
      throw new Error(`loop cut traversal reached ambiguous/non-manifold geometric edge '${exit.geometricEdgeId}'`);
    }
    cellId = nextCellIds[0];
    entryEdgeId = exit.geometricEdgeId;
  }
}

/**
 * Discover the valid logical-quad strip or ring crossing a selected derived
 * geometric edge. Pure/read-only; optional snapshots avoid recomputing the
 * same adjacency and logical-cell discovery for callers that already have it.
 */
export function discoverLoopCutTraversal(
  topology,
  startEdgeId,
  {
    adjacency = computeGeometricAdjacency(topology),
    logicalCells = discoverLogicalQuadCells(topology, adjacency),
  } = {},
) {
  if (typeof startEdgeId !== 'string' || startEdgeId.length === 0) {
    throw new Error('loop cut traversal start edge id must be a non-empty string');
  }
  const indexes = traversalIndexes(topology, adjacency, logicalCells);
  if (!indexes.geometricEdgesById.has(startEdgeId)) {
    if (adjacency.diagonalTopologyEdgeIds.includes(startEdgeId)) {
      throw new Error(`loop cut traversal start edge '${startEdgeId}' is an internal triangulation diagonal`);
    }
    throw new Error(`unknown geometric edge '${startEdgeId}'`);
  }

  const startCellIds = indexes.cellsByGeometricEdgeId.get(startEdgeId) ?? [];
  if (startCellIds.length === 0) {
    throw new Error(`geometric edge '${startEdgeId}' does not border a valid logical quad cell`);
  }
  if (startCellIds.length > 2) {
    throw new Error(`loop cut traversal start edge '${startEdgeId}' is ambiguous/non-manifold`);
  }

  const first = walkFromStart(indexes, startCellIds[0], startEdgeId);
  let canonical;
  let status;
  if (first.status === 'closed') {
    canonical = canonicalizeClosed(first.cellIds, first.crossedEdgeIds);
    status = 'closed';
  } else if (startCellIds.length === 1) {
    canonical = canonicalizeOpen(first.cellIds, first.crossedEdgeIds);
    status = 'open';
  } else {
    const second = walkFromStart(indexes, startCellIds[1], startEdgeId);
    if (second.status !== 'open') {
      throw new Error(`loop cut traversal has inconsistent paths from geometric edge '${startEdgeId}'`);
    }
    const repeatedCellId = first.cellIds.find((cellId) => second.cellIds.includes(cellId));
    if (repeatedCellId) {
      throw new Error(`loop cut traversal reached logical cell '${repeatedCellId}' from both directions`);
    }
    canonical = canonicalizeOpen(
      [...first.cellIds].reverse().concat(second.cellIds),
      [...first.crossedEdgeIds].reverse().concat(second.crossedEdgeIds.slice(1)),
    );
    status = 'open';
  }

  return { status, startEdgeId, ...canonical };
}
